import OnboardingSelectExchange from "features/onboarding/connectExchange/OnboardingSelectExchange.tsx";
import React, { useContext } from "react";
import AccountFormProvider from "@shared/features/common/AccountFormProvider.tsx";
import styled, { useTheme } from "styled-components";
import { StyledOnboardingSteps } from "features/onboarding/OnboardingSteps.tsx";
import InvitingUserSummary from "features/configuration/InviteExternalUsers/InvitingUserSummary.tsx";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery.ts";
import OnboardingConnectExchange from "features/onboarding/connectExchange/OnboardingConnectExchange.tsx";
import useCreateAccountMessage from "features/configuration/accounts/CreateAccount/useCreateAccountMessage.tsx";
import useFormattedNotification from "utils/hooks/useFormattedNotification.tsx";
import ExternalConnectAccountProvider from "features/configuration/InviteExternalUsers/ExternalConnectAccountProvider.tsx";
import SuccessPage from "features/configuration/InviteExternalUsers/SuccessPage.tsx";
import cornixLogo from "images/svg_logo.svg";
import cornixWhiteLogo from "images/svg_white_logo.svg";
import ArrowLeftOutlined from "@ant-design/icons/ArrowLeftOutlined";
import { AccountFormContext } from "@shared/features/common/AccountForm.context.ts";
import useMedia from "utils/hooks/useMedia.ts";
import { GENERAL_BACKEND_ERROR, MOBILE_SEARCH } from "common/constants.ts";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import { ConnectExternalAccountStep } from "@shared/features/configuration/types.ts";
import { BrokenLink } from "common/Icons/Icons.tsx";
import Title from "common/Typography/Title.tsx";
import Text from "common/Typography/Text.tsx";
import { useWindowSize } from "react-use";

const ExternalConnectAccount = () => {
  return (
    <ExternalConnectAccountProvider>
      <AccountFormProvider>
        <InnerExternalConnectAccount />
      </AccountFormProvider>
    </ExternalConnectAccountProvider>
  );
};

export default ExternalConnectAccount;

const InnerExternalConnectAccount = () => {
  const theme = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { appendQuery } = useHistoryWithQuery();
  const { getSuccessMessage, contextHolder } = useCreateAccountMessage();
  const { getErrorNotification, contextHolder: errorContextHolder } =
    useFormattedNotification();
  const { resetFormValues } = useContext(AccountFormContext)!;
  const { step, setStep, isError, errorMessage } = useContext(
    ExternalConnectAccountContext
  )!;
  const { height } = useWindowSize();

  const onSelectExchange = (primaryExchange: string) => {
    appendQuery({ exchange: primaryExchange });
    setStep(ConnectExternalAccountStep.CONNECT_EXCHANGE);
  };

  const onBackClick = () => {
    resetFormValues();
    setStep(ConnectExternalAccountStep.SELECT_EXCHANGE);
    appendQuery(undefined, ["exchange"]);
  };

  const getBackButton = () => {
    if (step !== ConnectExternalAccountStep.CONNECT_EXCHANGE) return null;
    return (
      <BackWrapper>
        <BackButton onClick={onBackClick}>
          <ArrowLeftOutlined style={{ marginRight: 8 }} />
          Select Exchange
        </BackButton>
      </BackWrapper>
    );
  };

  const getContent = () => {
    if (isError) {
      const isGeneralError = errorMessage === GENERAL_BACKEND_ERROR;
      return (
        <ErrorWrapper>
          <div style={{ marginTop: -(height / 8), maxWidth: 370 }}>
            <BrokenLink
              style={{
                width: isMobile ? 80 : 140,
                height: "auto",
                marginBottom: 16,
              }}
            />
            <Title level={isMobile ? 2 : 1} style={{ textAlign: "center" }}>
              {isGeneralError ? "Something Went Wrong" : "Invitation Not Valid"}
            </Title>
            <Text style={{ maxWidth: isMobile ? 300 : 360 }}>
              {isGeneralError
                ? "Please try again in few minutes or contact our support."
                : errorMessage}
            </Text>
          </div>
        </ErrorWrapper>
      );
    }
    return (
      <ContentWrapper>
        {getBackButton()}
        {contextHolder}
        {errorContextHolder}
        {step !== ConnectExternalAccountStep.SUCCESS && (
          <>
            <StyledOnboardingSteps
              size="small"
              labelPlacement="vertical"
              current={step}
              responsive={false}
              $isMobile={false}
              items={[
                { title: "Select Exchange" },
                { title: "Connect Exchange" },
              ]}
              style={{ maxWidth: 250 }}
            />
            <InvitingUserSummary
              style={{ margin: isMobile ? "16px 0" : "32px 0" }}
            />
          </>
        )}

        {step === ConnectExternalAccountStep.SELECT_EXCHANGE && (
          <OnboardingSelectExchange onSelectExchange={onSelectExchange} />
        )}
        {step === ConnectExternalAccountStep.CONNECT_EXCHANGE && (
          <OnboardingConnectExchange
            getErrorNotification={getErrorNotification}
            getSuccessMessage={getSuccessMessage}
          />
        )}
        {step === ConnectExternalAccountStep.SUCCESS && <SuccessPage />}
      </ContentWrapper>
    );
  };

  return (
    <Wrapper>
      <div style={{ width: "100%" }}>
        <Logo src={theme.isDarkMode ? cornixWhiteLogo : cornixLogo} />
      </div>
      {getContent()}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
`;
const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex: 1;
`;
const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 20px;
  overflow-y: auto;
  @media (max-width: 700px) {
    padding-top: 8px;
  }
`;
const Logo = styled.img`
  height: 50px;
  margin: 10px 16px;
`;
const BackWrapper = styled.div`
  width: 100%;
  padding-bottom: 16px;
  @media (min-width: 700px) {
    padding-bottom: 0;
  }
`;
const BackButton = styled.div`
  display: flex;
  align-items: center;
  color: ${(p) => p.theme.colors.primaryBlue};
  cursor: pointer;
`;
